import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, wanneer } from '../i18n.js';
import { ERNST } from '../components/Status.jsx';
import { ICOON } from '../components/iconen.jsx';
import Wachtwoordveld from '../components/Wachtwoordveld.jsx';

/**
 * Beheer › Meldingen (algemeen): de kantooruren en de kanalen (de mailserver en de Webex-bot). De
 * **meldingsregels zelf staan per klant** onder Beheer › Klant (docs/besluiten/meldingen-per-klant-instellen.md);
 * dit scherm gaat alleen over wat voor alle klanten geldt: wanneer er iemand kijkt en hoe ZERO de kanalen bereikt.
 *
 * Een wachtwoord of token komt nooit terug naar dit scherm, alleen óf het er is.
 */
const KANAAL = { mail: 'Mail', webex: 'Webex' };

export default function BeheerMeldingen() {
  const DAGEN = [[1, t('ma')], [2, t('di')], [3, t('wo')], [4, t('do')], [5, t('vr')], [6, t('za')], [7, t('zo')]];
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['beheer-meldingen'], queryFn: api.beheerMeldingen, refetchInterval: 10_000 });
  const [v, zetV] = useState(null);
  const [melding, zetMelding] = useState(null);
  const [venster, zetVenster] = useState(null);
  useEffect(() => { if (q.data && !v) zetV({ uren: q.data.data.officeHours, m: q.data.data.notifications }); }, [q.data, v]);
  if (q.isPending || !v) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const { channels, channelSettings, now, recent } = q.data.data;
  const tandwiel = (kanaal, naam) => (
    <button type="button" className="tandwiel" aria-label={t('Instellingen van {kanaal}', { kanaal: naam })}
      title={t('Instellingen van {kanaal}', { kanaal: naam })} onClick={() => zetVenster(kanaal)}>{ICOON.tandwiel}</button>
  );
  const herkomst = (k) => (channelSettings[k].bron === 'beheer'
    ? t('Ingesteld onder Beheer, {wanneer}.', { wanneer: wanneer(channelSettings[k].gewijzigd) })
    : t('Uit de omgeving op de server (.env).'));
  const wissel = (lijst, w, aan) => (aan ? [...new Set([...lijst, w])] : lijst.filter((x) => x !== w));
  const zet = (pad, waarde) => zetV((x) => {
    const y = structuredClone(x); let o = y; for (const k of pad.slice(0, -1)) o = o[k]; o[pad.at(-1)] = waarde; return y;
  });
  const ernstVinkjes = () => (
    <div className="themarij" style={{ gap: 12 }}>
      {Object.entries(ERNST).map(([k, l]) => (
        <label key={k} className="vink"><input type="checkbox" checked={v.m.webex.ernst.includes(k)}
          onChange={(e) => zet(['m', 'webex', 'ernst'], wissel(v.m.webex.ernst, k, e.target.checked))} />{t(l)}</label>
      ))}
    </div>
  );

  const bewaar = async () => {
    zetMelding(null);
    try {
      await api.zetBeheerMeldingen({ officeHours: v.uren, notifications: v.m });
      zetMelding({ goed: true, tekst: t('Opgeslagen.') });
      await client.invalidateQueries({ queryKey: ['beheer-meldingen'] });
    } catch (e) {
      zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) });
    }
  };
  const test = async () => {
    zetMelding(null);
    try {
      const { data } = await api.testMeldingAlgemeen();
      zetMelding({ goed: true, tekst: t('Testbericht naar {n} bestemming(en). De uitkomst staat hieronder.', { n: data.queued }) });
      await client.invalidateQueries({ queryKey: ['beheer-meldingen'] });
    } catch (e) { zetMelding({ goed: false, tekst: t(e.message) }); }
  };

  return (
    <>
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 14 }}>{melding.tekst}</div>}
      <section className="card setblok">
        <span className="label">{t('Kantooruren')}</span>
        <div className="setrij">
          <div className="setkop"><b>{t('Wanneer er iemand kijkt')}</b>
            <span>{now.inside ? t('Nu is het binnen kantooruren.') : t('Nu is het buiten kantooruren; de volgende werkdag begint {wanneer}.', { wanneer: wanneer(now.nextStart) })}</span></div>
          <div>
            <div className="themarij" style={{ gap: 10, marginBottom: 8 }}>
              {DAGEN.map(([d, l]) => (
                <label key={d} className="vink"><input type="checkbox" checked={v.uren.dagen.includes(d)}
                  onChange={(e) => zet(['uren', 'dagen'], wissel(v.uren.dagen, d, e.target.checked).sort())} />{l}</label>
              ))}
            </div>
            <div className="rij">
              <input className="veld" style={{ minWidth: 0, width: 90 }} value={v.uren.van} onChange={(e) => zet(['uren', 'van'], e.target.value)} aria-label={t('Van')} />
              <span className="muted">{t('tot')}</span>
              <input className="veld" style={{ minWidth: 0, width: 90 }} value={v.uren.tot} onChange={(e) => zet(['uren', 'tot'], e.target.value)} aria-label={t('Tot')} />
            </div>
          </div>
        </div>
      </section>

      <section className="card setblok" style={{ marginTop: 18 }}>
        <span className="label">{t('Algemene meldingen (naar ADTS, voor alle klanten)')}</span>
        <p className="noot" style={{ marginTop: 0 }}>{t('Deze meldingen gaan naar het SOC van ADTS en gelden voor alle klanten. Een klant kan daarnaast eigen meldingen hebben (Beheer › Klanten › Meldingen); dezelfde bestemming krijgt het maar één keer.')}</p>
        <div className="setrij">
          <div className="setkop"><b>{t('Webex buiten kantooruren')}</b><span>{t('Meteen, dag en nacht (24/7), of vasthouden tot de werkdag begint. Mail gaat per regel, elk met zijn eigen tijd (hieronder). Een geslaagde VPN-login na een reeks mislukte gaat altijd meteen naar Webex.')}</span></div>
          <div className="themarij">
            {[['webex', 'Webex meteen, 24/7'], ['vasthouden', 'Webex vasthouden tot de werkdag begint']].map(([k, l]) => (
              <label key={k} className="vink"><input type="radio" name="buiten" checked={v.m.buitenKantooruren === k} onChange={() => zet(['m', 'buitenKantooruren'], k)} />{t(l)}</label>
            ))}
          </div>
        </div>
        <div className="setrij">
          <div className="setkop"><b>Webex</b><span>{t('De Webex-ruimte (room ID) van het SOC, en welke ernst er naartoe gaat.')}</span></div>
          <div className="form" style={{ gap: 10 }}>
            <label className="vink"><input type="checkbox" checked={v.m.webex.aan} onChange={(e) => zet(['m', 'webex', 'aan'], e.target.checked)} />{t('Aan')}</label>
            <label className="field"><span>{t('Ruimte (room ID)')}</span>
              <input className="input mono" value={v.m.webex.ruimte} onChange={(e) => zet(['m', 'webex', 'ruimte'], e.target.value)} placeholder="Y2lzY29zcGFyazovL3VzL1JPT00v…" /></label>
            {ernstVinkjes()}
          </div>
        </div>
        <div className="setrij">
          <div className="setkop"><b>{t('Mail')}</b><span>{t('Mailregels: per regel wie, welke alerts en wanneer.')}</span></div>
          <div className="form" style={{ gap: 10 }}>
            <label className="vink"><input type="checkbox" checked={v.m.mail.aan} onChange={(e) => zet(['m', 'mail', 'aan'], e.target.checked)} />{t('Aan')}</label>
            <MailRegels regels={v.m.mail.regels} modules={q.data.data.modules} uren={v.uren} zet={(r) => zet(['m', 'mail', 'regels'], r)} />
          </div>
        </div>
        <div className="actions">
          <button className="btn primary" onClick={bewaar}>{t('Opslaan')}</button>
          <button className="btn" onClick={test}>{t('Stuur een testbericht')}</button>
        </div>
      </section>

      <section className="card setblok" style={{ marginTop: 18 }}>
        <span className="label">{t('Kanalen')}</span>
        <p className="noot" style={{ marginTop: 0 }}>{t('De mailserver en de Webex-bot (het transport) gelden voor alle klanten. Welke ruimte en welke mailadressen er heen gaan, stel je hierboven (algemeen) of per klant in.')}</p>
        <div className="setrij">
          <div className="setkop"><b className="kanaalkop">Webex {tandwiel('webex', 'Webex')}</b>
            <span>{channels.webex ? t('De bot is ingesteld.') : t('Er is nog geen token voor de Webex-bot. Berichten krijgen de stand "uit".')} {herkomst('webex')}</span></div>
          <div />
        </div>
        <div className="setrij">
          <div className="setkop"><b className="kanaalkop">{t('Mail')} {tandwiel('mail', t('Mail'))}</b>
            <span>{channels.mail ? t('Via {server}, afzender {afzender}.', { server: `${channelSettings.mail.host}:${channelSettings.mail.poort}`, afzender: channelSettings.mail.afzender || '—' })
              : t('Er is nog geen mailserver. Berichten krijgen de stand "uit".')} {herkomst('mail')}</span></div>
          <div />
        </div>
      </section>

      {venster && <Kanaalvenster kanaal={venster} nu={channelSettings[venster]} onDicht={() => zetVenster(null)}
        onOpgeslagen={async () => {
          zetVenster(null);
          zetMelding({ goed: true, tekst: t('Opgeslagen.') });
          await client.invalidateQueries({ queryKey: ['beheer-meldingen'] });
        }} />}

      <section className="card" style={{ marginTop: 18 }}>
        <header><h2>{t('Laatste meldingen')}</h2></header>
        {recent.length ? (
          <table>
            <thead><tr><th>{t('Wanneer')}</th><th>{t('Kanaal')}</th><th>{t('Naar')}</th><th>{t('Alert')}</th><th>{t('Stand')}</th></tr></thead>
            <tbody>{recent.map((n) => (
              <tr key={n.id}>
                <td className="mono muted">{wanneer(n.sentAt ?? n.queuedAt)}</td>
                <td>{KANAAL[n.channel]}</td>
                <td className="soft" style={{ overflowWrap: 'anywhere' }} title={n.recipient}>{n.channel === 'webex' ? t('Webex-ruimte van het SOC') : n.recipient}</td>
                <td className="mono">{n.alert ?? t('test')}</td>
                <td>{t(n.status)}{n.error && <><br /><span className="muted" style={{ fontSize: 12 }}>{t(n.error)}</span></>}
                  {n.status === 'wacht' && n.holdUntil && <><br /><span className="muted" style={{ fontSize: 12 }}>{t('vastgehouden tot {wanneer}', { wanneer: wanneer(n.holdUntil) })}</span></>}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <p className="empty">{t('Nog geen meldingen.')}</p>}
      </section>
    </>
  );
}

/**
 * Het venster achter het tandwiel: hoe ZERO het kanaal bereikt. Nakijken probeert het met wat er in het venster staat,
 * zonder op te slaan en zonder een bericht te sturen. Een wachtwoord of token laat je leeg om het te houden.
 */
function Kanaalvenster({ kanaal, nu, onDicht, onOpgeslagen }) {
  const mail = kanaal === 'mail';
  const [v, zetV] = useState(mail
    ? { host: nu.host ?? '', poort: nu.poort ?? 25, beveiliging: nu.beveiliging ?? 'starttls', afzender: nu.afzender ?? '', gebruiker: nu.gebruiker ?? '', wachtwoord: '' }
    : { token: '' });
  const [weg, zetWeg] = useState(false);
  const [uitkomst, zetUitkomst] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const zet = (veld) => (w) => { zetV((x) => ({ ...x, [veld]: w })); zetUitkomst(null); };
  const geheim = mail ? 'wachtwoord' : 'token';
  /* Leeg en niet weggehaald: het geheim blijft wat het was. */
  const invoer = () => {
    const { [geheim]: g, ...rest } = v;
    return { ...rest, ...(mail ? { poort: Number(v.poort) } : {}), ...(g ? { [geheim]: g } : weg ? { [geheim]: '' } : {}) };
  };
  const doe = async (fn) => {
    zetBezig(true); zetUitkomst(null);
    try { await fn(); } catch (e) { zetUitkomst({ goed: false, fout: t(e.details ? Object.values(e.details)[0] : e.message) }); } finally { zetBezig(false); }
  };
  const naam = mail ? t('Mail') : 'Webex';

  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onDicht(); }}>
      <form className="modal" onSubmit={(e) => { e.preventDefault(); doe(async () => { await api.zetKanaal(kanaal, invoer()); await onOpgeslagen(); }); }}>
        <h2 style={{ margin: 0 }}>{t('Instellingen van {kanaal}', { kanaal: naam })}</h2>
        {mail ? (
          <>
            <div className="rij" style={{ gap: 10 }}>
              <label className="field" style={{ flex: 1 }}><span>{t('Mailserver')}</span>
                <input className="input mono" value={v.host} onChange={(e) => zet('host')(e.target.value)} placeholder="smtp010.adts.nl" /></label>
              <label className="field" style={{ width: 90 }}><span>{t('Poort')}</span>
                <input className="input mono" inputMode="numeric" value={v.poort} onChange={(e) => zet('poort')(e.target.value.replace(/\D/g, ''))} /></label>
            </div>
            <label className="field"><span>{t('Beveiliging')}</span>
              <select className="input" value={v.beveiliging} onChange={(e) => zet('beveiliging')(e.target.value)}>
                <option value="starttls">{t('STARTTLS (verplicht)')}</option>
                <option value="tls">{t('TLS vanaf het begin (meestal poort 465)')}</option>
                <option value="geen">{t('Geen (alleen op een vertrouwd netwerk)')}</option>
              </select></label>
            <label className="field"><span>{t('Afzender')}</span>
              <input className="input" value={v.afzender} onChange={(e) => zet('afzender')(e.target.value)} placeholder="ADTS ZERO <zero@adts.nl>" /></label>
            <label className="field"><span>{t('Gebruikersnaam (leeg: zonder aanmelden)')}</span>
              <input className="input" value={v.gebruiker} onChange={(e) => zet('gebruiker')(e.target.value)} autoComplete="off" /></label>
          </>
        ) : null}
        <label className="field"><span>{mail ? t('Wachtwoord') : t('Token van de bot')}</span>
          <Wachtwoordveld waarde={v[geheim]} zet={(w) => { zet(geheim)(w); zetWeg(false); }} autoComplete="new-password" />
          <span className="muted" style={{ fontSize: 12 }}>
            {nu[geheim] && !weg ? <>{t('Er staat er een; leeg laten houdt hem.')}{' '}
              <button type="button" className="namelink gevaar" onClick={() => { zetWeg(true); zetUitkomst(null); }}>{t('Weghalen')}</button></>
              : weg ? t('Wordt weggehaald bij opslaan.') : t('Nog niet ingesteld.')}
          </span></label>
        {!mail && <p className="muted" style={{ fontSize: 13, margin: 0 }}>{t('De ruimte en welke alerts erheen gaan, stel je per klant in onder Beheer › Klant.')}</p>}
        {uitkomst && <div className={`melder ${uitkomst.goed ? 'blauw' : 'rood'}`}>
          {uitkomst.goed ? (mail ? t('De mailserver {naam} neemt het aan.', { naam: uitkomst.naam }) : t('Het token hoort bij {naam}.', { naam: uitkomst.naam })) : uitkomst.fout}</div>}
        <div className="foot">
          <button type="button" className="btn" onClick={onDicht}>{t('Annuleren')}</button>
          <button type="button" className="btn" disabled={bezig}
            onClick={() => doe(async () => zetUitkomst((await api.controleerKanaal(kanaal, invoer())).data))}>{bezig ? t('Bezig…') : t('Nakijken')}</button>
          <button className="btn primary" disabled={bezig}>{t('Opslaan')}</button>
        </div>
      </form>
    </div>
  );
}

/**
 * Mailregels (src/modules/meldingen/regels.js): per regel wie, welke alerts (ernst, modules) en wanneer. Avond en weekend
 * zijn snelkeuzes die dagen en tijden invullen; daarna kun je ze aanpassen. Gebruikt door Beheer › Klant (per klant).
 */
const snelkeuze = {
  avond: { wanneer: 'eigen', dagen: [1, 2, 3, 4, 5], van: '18:00', tot: '08:00' },
  weekend: { wanneer: 'eigen', dagen: [6, 7], van: '00:00', tot: '00:00' },
};
const nieuweRegel = { naam: '', ontvangers: [], ernst: ['critical', 'serious'], modules: [], wanneer: 'altijd', dagen: [], van: '00:00', tot: '00:00', vasthouden: false };

export function MailRegels({ regels, modules, uren, zet }) {
  const DAGEN = [[1, t('ma')], [2, t('di')], [3, t('wo')], [4, t('do')], [5, t('vr')], [6, t('za')], [7, t('zo')]];
  const wijzig = (i, velden) => zet(regels.map((r, j) => (j === i ? { ...r, ...velden } : r)));
  const wissel = (lijst, w, aan) => (aan ? [...new Set([...lijst, w])] : lijst.filter((x) => x !== w));
  const keuze = (r) => (r.wanneer !== 'eigen' ? r.wanneer
    : Object.entries(snelkeuze).find(([, s]) => s.van === r.van && s.tot === r.tot && s.dagen.join() === [...r.dagen].sort().join())?.[0] ?? 'eigen');
  const tijdVan = (r) => {
    if (r.wanneer === 'kantooruren') return t('Binnen kantooruren ({van} tot {tot}).', { van: uren.van, tot: uren.tot });
    if (r.wanneer === 'buiten') return t('Buiten kantooruren: avonden, nachten en het weekend.');
    return t('Altijd, dag en nacht.');
  };
  return (
    <div className="stack" style={{ gap: 12 }}>
      {!regels.length && <p className="muted" style={{ margin: 0 }}>{t('Nog geen mailregels: er gaat geen mail uit.')}</p>}
      {regels.map((r, i) => (
        <fieldset key={i} className="mailregel">
          <div className="rij" style={{ gap: 10 }}>
            <input className="veld" style={{ flex: 1 }} value={r.naam} onChange={(e) => wijzig(i, { naam: e.target.value })} placeholder={t('Naam, bijvoorbeeld Weekenddienst')} aria-label={t('Naam van de regel')} />
            <button type="button" className="namelink gevaar" onClick={() => zet(regels.filter((_, j) => j !== i))}>{t('Verwijderen')}</button>
          </div>
          <label className="field"><span>{t('Naar (één adres per regel)')}</span>
            <textarea rows={2} value={r.ontvangers.join('\n')} onChange={(e) => wijzig(i, { ontvangers: e.target.value.split(/\s+/).filter(Boolean) })} placeholder="dienst@adts.nl" /></label>
          <div className="themarij" style={{ gap: 12 }}>
            <span className="muted">{t('Ernst')}</span>
            {Object.entries(ERNST).map(([k, l]) => (
              <label key={k} className="vink"><input type="checkbox" checked={r.ernst.includes(k)} onChange={(e) => wijzig(i, { ernst: wissel(r.ernst, k, e.target.checked) })} />{t(l)}</label>
            ))}
          </div>
          <div className="themarij" style={{ gap: 12 }}>
            <span className="muted">{t('Modules')}</span>
            <label className="vink"><input type="checkbox" checked={!r.modules.length} onChange={(e) => wijzig(i, { modules: e.target.checked ? [] : [modules[0].id] })} />{t('Alle')}</label>
            {r.modules.length > 0 && modules.map((m) => (
              <label key={m.id} className="vink"><input type="checkbox" checked={r.modules.includes(m.id)} onChange={(e) => wijzig(i, { modules: wissel(r.modules, m.id, e.target.checked) })} />{t(m.label)}</label>
            ))}
          </div>
          <div className="rij" style={{ gap: 10, flexWrap: 'wrap' }}>
            <span className="muted">{t('Wanneer')}</span>
            <select className="input" style={{ width: 'auto' }} value={keuze(r)} onChange={(e) => {
              const k = e.target.value;
              wijzig(i, snelkeuze[k] ?? (k === 'eigen' ? { wanneer: 'eigen', dagen: r.dagen.length ? r.dagen : [1, 2, 3, 4, 5], van: r.van, tot: r.tot } : { wanneer: k }));
            }}>
              <option value="altijd">{t('Altijd (24/7)')}</option>
              <option value="kantooruren">{t('Binnen kantooruren')}</option>
              <option value="buiten">{t('Buiten kantooruren')}</option>
              <option value="avond">{t('Avond en nacht (ma–vr 18:00–08:00)')}</option>
              <option value="weekend">{t('Weekend (za en zo)')}</option>
              <option value="eigen">{t('Eigen dagen en tijden')}</option>
            </select>
            {r.wanneer !== 'altijd' && r.wanneer !== 'buiten' && (
              <label className="vink"><input type="checkbox" checked={r.vasthouden} onChange={(e) => wijzig(i, { vasthouden: e.target.checked })} />{t('Daarbuiten bewaren tot het zo ver is')}</label>
            )}
          </div>
          {r.wanneer === 'eigen' ? (
            <div className="rij" style={{ gap: 10, flexWrap: 'wrap' }}>
              {DAGEN.map(([d, l]) => (
                <label key={d} className="vink"><input type="checkbox" checked={r.dagen.includes(d)} onChange={(e) => wijzig(i, { dagen: wissel(r.dagen, d, e.target.checked).sort() })} />{l}</label>
              ))}
              <input className="veld" style={{ minWidth: 0, width: 80 }} value={r.van} onChange={(e) => wijzig(i, { van: e.target.value })} aria-label={t('Van')} />
              <span className="muted">{t('tot')}</span>
              <input className="veld" style={{ minWidth: 0, width: 80 }} value={r.tot} onChange={(e) => wijzig(i, { tot: e.target.value })} aria-label={t('Tot')} />
              <span className="muted klein">{t('Over middernacht mag (18:00 tot 08:00); van gelijk aan tot is de hele dag.')}</span>
            </div>
          ) : <p className="muted klein" style={{ margin: 0 }}>{tijdVan(r)}</p>}
        </fieldset>
      ))}
      <div><button type="button" className="btn" onClick={() => zet([...regels, { ...nieuweRegel }])}>{t('+ Mailregel')}</button></div>
    </div>
  );
}

export { nieuweRegel };
